import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { qcApi } from "./client";

export const qcKeys = {
  all: ["qc"] as const,
  myInspector: () => [...qcKeys.all, "me", "inspector"] as const,
  inspectorStandards: () => [...qcKeys.all, "inspector", "standards"] as const,
  inspectorStandardDetail: (id: string) =>
    [...qcKeys.all, "inspector", "standard", id] as const,
  workItemTypes: () => [...qcKeys.all, "workItemTypes"] as const,
  myRecords: (params?: { limit?: number; offset?: number }) =>
    [...qcKeys.all, "records", "mine", params?.limit, params?.offset] as const,
  myRecordDetail: (id: string) =>
    [...qcKeys.all, "records", "detail", id] as const,
};

export function useQcInspectorStatusQuery(options: { enabled: boolean }) {
  return useQuery({
    queryKey: qcKeys.myInspector(),
    queryFn: () => qcApi.getMyInspectorStatus(),
    enabled: options.enabled,
    staleTime: 1000 * 60 * 2,
  });
}

export function useInspectorStandardsQuery(options: { enabled: boolean }) {
  return useQuery({
    queryKey: qcKeys.inspectorStandards(),
    queryFn: () => qcApi.listInspectorStandards(),
    enabled: options.enabled,
    staleTime: 1000 * 60 * 2,
  });
}

export function useInspectorStandardDetailQuery(
  standardId: string,
  options: { enabled: boolean },
) {
  return useQuery({
    queryKey: qcKeys.inspectorStandardDetail(standardId),
    queryFn: () => qcApi.getInspectorStandardDetail(standardId),
    enabled: options.enabled && Boolean(standardId),
  });
}

export function useWorkItemTypesQuery(options: { enabled: boolean }) {
  return useQuery({
    queryKey: qcKeys.workItemTypes(),
    queryFn: () => qcApi.listWorkItemTypes(),
    enabled: options.enabled,
    staleTime: 1000 * 60 * 30,
  });
}

export function useMyQcRecordsQuery(options: {
  enabled: boolean;
  limit?: number;
  offset?: number;
}) {
  return useQuery({
    queryKey: qcKeys.myRecords({
      limit: options.limit,
      offset: options.offset,
    }),
    queryFn: () =>
      qcApi.listMyRecords({
        limit: options.limit,
        offset: options.offset,
      }),
    enabled: options.enabled,
    staleTime: 1000 * 60,
  });
}

export function useMyQcRecordDetailQuery(
  recordId: string,
  options: { enabled: boolean },
) {
  return useQuery({
    queryKey: qcKeys.myRecordDetail(recordId),
    queryFn: () => qcApi.getMyRecord(recordId),
    enabled: options.enabled && Boolean(recordId),
    staleTime: 1000 * 60,
  });
}

export function useCreateQcRecordMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: qcApi.createRecord,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qcKeys.all });
    },
  });
}
